import {
  AttendanceType,
  ConferenceLinkType,
  RepeatOptions,
} from '@prisma/client'
import { clsx, type ClassValue } from 'clsx'
import { DateTime } from 'luxon'
import { ReadonlyURLSearchParams } from 'next/navigation'
import { twMerge } from 'tailwind-merge'
import { calendarColors } from './consts'
import { ActionResult, SuccessResponse } from './types'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
export function cleanAndCompareText(text1: string, text2: string): boolean {
  const cleanText = (str: string) =>
    str
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/\s+/g, '') // Remove all whitespace (including newlines and extra spaces)
      .trim() // Trim leading/trailing spaces

  return cleanText(text1) === cleanText(text2)
}

export function debounce<T extends (...args: any[]) => void>(fn: T, ms = 0) {
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => fn.apply(this, args), ms)
  }
}

export function getInitials(name: string) {
  if (!name) return ''

  const nameArray = name.trim().split(' ')

  if (nameArray.length < 2) {
    return nameArray[0].charAt(0).toUpperCase()
  }

  const firstNameInitial = nameArray[0].charAt(0).toUpperCase()
  const lastNameInitial = nameArray[nameArray.length - 1]
    .charAt(0)
    .toUpperCase()

  return `${firstNameInitial}${lastNameInitial}`
}

export const formatMoney = (value: string, includeZeros?: boolean) => {
  // Replace all non-numeric characters except for the first period and the negative sign
  const numberValue = parseFloat(value.replace(/[^0-9.-]/g, ''))

  // Handle NaN cases by returning zero formatted as currency
  if (isNaN(numberValue)) {
    return (0).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    })
  }

  // Format the number as currency
  let formattedValue = numberValue.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  })

  // Remove trailing ".00" if present
  if (formattedValue.includes('.00') && !includeZeros) {
    formattedValue = formattedValue.replace('.00', '')
  }

  return formattedValue
}

export const getCurrentMonthNameInTimezone = (timezone: string) => {
  const now = DateTime.now().setZone(timezone)
  return now.toFormat('MMMM') // 'MMMM' gives the full month name
}

export const hexToRgba = (hex: any, opacity: any) => {
  if (hex) {
    hex = hex.replace('#', '')

    // Parse the r, g, b values
    let r = parseInt(hex.substring(0, 2), 16)
    let g = parseInt(hex.substring(2, 4), 16)
    let b = parseInt(hex.substring(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, ${opacity})`
  }

  return ''
}

export const brightenHex = (hex: string, percentage: number = 20) => {
  // Remove the '#' if it's present
  hex = hex.replace(/^#/, '')

  // Parse the hex into RGB components
  let r = parseInt(hex.substring(0, 2), 16)
  let g = parseInt(hex.substring(2, 4), 16)
  let b = parseInt(hex.substring(4, 6), 16)

  // Increase each component by the given percentage
  r = Math.min(255, Math.round(r + (percentage / 100) * r))
  g = Math.min(255, Math.round(g + (percentage / 100) * g))
  b = Math.min(255, Math.round(b + (percentage / 100) * b))

  // Convert back to hex and pad with 0 if needed
  const newHex =
    '#' +
    r.toString(16).padStart(2, '0') +
    g.toString(16).padStart(2, '0') +
    b.toString(16).padStart(2, '0')

  return newHex
}

export const toTitleCase = (str: string) => {
  return str
    .toLowerCase()
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, (char) => char.toUpperCase()) // Capitalize the first letter of each word
}

export const formatEnumValue = (value: string) => {
  return value
    .toLowerCase()
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, (char) => char.toUpperCase()) // Capitalize first letter of each word
}

export function isSuccessResult<T>(
  response: ActionResult<T>,
): response is SuccessResponse<T> {
  return !response.error
}

export const abbreviateMonthInText = (text: string) => {
  const monthAbbreviations: any = {
    January: 'Jan',
    February: 'Feb',
    March: 'Mar',
    April: 'Apr',
    May: 'May',
    June: 'Jun',
    July: 'Jul',
    August: 'Aug',
    September: 'Sep',
    October: 'Oct',
    November: 'Nov',
    December: 'Dec',
  }

  // Replace any full month name with its abbreviation using regex
  return text.replace(
    /\b(January|February|March|April|May|June|July|August|September|October|November|December)\b/g,
    (match) => monthAbbreviations[match],
  )
}

export const replaceNullWithUndefined = (obj: Object) => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      value === null ? undefined : value,
    ]),
  )
}

export function formatString(input: string | undefined) {
  // Convert the first letter to uppercase and the rest to lowercase
  if (!input) {
    return ''
  }
  return input
    .toLowerCase() // Convert the entire string to lowercase
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/^\w/, (c) => c.toUpperCase()) // Capitalize the first letter
}

export function hexToCalendarColorExact(hex: string | null) {
  if (!hex) {
    return null
  }
  // Convert hex to lowercase to ensure case insensitivity
  hex = hex.toLowerCase()
  const apiNumber = calendarColors.find((cc) => {
    if (cc.value.toLowerCase() === hex) {
      return cc.apiNumber
    }
  })

  return apiNumber ? apiNumber.apiNumber : null
}

export const createQueryString = (
  name: string,
  value: string,
  searchParams: ReadonlyURLSearchParams,
) => {
  const params = new URLSearchParams(searchParams.toString())
  params.set(name, value)

  return params.toString()
}

export function convertToMilitaryTime(time: string): string {
  // Match the time and the AM/PM part
  let match: any = time.match(/^(\d{1,2}):(\d{2})(AM|PM)$/i)

  if (!match) {
    throw new Error(
      "Invalid time format. Expected format: 'h:mmAM' or 'h:mmPM'",
    )
  }

  let [_, hour, minutes, period] = match
  hour = parseInt(hour, 10)

  if (period.toUpperCase() === 'PM' && hour !== 12) {
    hour += 12 // Convert PM hours to military time
  } else if (period.toUpperCase() === 'AM' && hour === 12) {
    hour = 0 // Midnight is 00:00 in military time
  }

  // Pad single-digit hours with a leading zero
  const formattedHour = hour.toString().padStart(2, '0')

  return `${formattedHour}:${minutes}`
}

export function getFriendlyAttendanceType(type: AttendanceType | undefined) {
  const attendanceMap = {
    UNRECORDED: 'Unrecorded',
    PRESENT: 'Present',
    ABSENT_BILLABLE: 'Absent (Billable)',
    ABSENT_NOT_BILLABLE: 'Absent (Not Billable)',
    CANCELLED: 'Cancelled',
  }

  if (type) {
    return attendanceMap[type]
  }

  return 'Unknown Status'
}

export function getFriendlyFrequency(frequency: RepeatOptions | undefined) {
  const repeatOptionMap: Partial<Record<RepeatOptions, string>> = {
    REPEATS_WEEKLY: 'Weekly',
    REPEATS_MONTHLY: 'Monthly',
  }
  if (frequency) {
    return repeatOptionMap[frequency]
  }
  return 'Unknown'
}

export function getFriendlyConferenceLinkType(
  type: ConferenceLinkType | undefined,
) {
  const typeMap: Partial<Record<ConferenceLinkType, string>> = {
    GOOGLE_MEET: 'Google Meet',
    ZOOM: 'Zoom',
    MICROSOFT_TEAMS: 'Microsoft Teams',
  }
  if (type) {
    return typeMap[type]
  }
  return 'Unknown'
}
